import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
// The SAME resizable image node the client editor uses - drag handle, delete
// button, width stored as style="width: Npx". This editor shipped with
// TipTap's plain Image (no handles, no width) and Bill found it on the first
// preview use, 2026-09-02; the node now lives in its own module so the two
// editors cannot differ on it again.
import ResizableImage from '../ResizableImage';
import Link from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import CodeBlock from '@tiptap/extension-code-block';
import Blockquote from '@tiptap/extension-blockquote';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import ScriptRefNode from './ScriptRefNode';
import PageLinkNode from './PageLinkNode';
import {
    FaBold, FaItalic, FaUnderline, FaStrikethrough, FaListUl, FaListOl,
    FaQuoteRight, FaMinus, FaCode, FaLink, FaUnlink, FaTable, FaFont,
    FaHighlighter, FaUndo, FaRedo, FaImage, FaPaperclip, FaFileAlt, FaFileCode,
    FaTrash
} from 'react-icons/fa';

/**
 * The TME Wiki page editor.
 *
 * Planning/TME-WIKI-2026-09-02.md §7, §10.1.
 *
 * ⚠️ `WikiPageEditor.js` IS NOT MODIFIED, AND THIS IS NOT A FORK OF IT. The
 * client wiki's editor is reused unchanged wherever it is used; this is a
 * SECOND editor, because the internal one needs extensions the client one must
 * never have - the inline script reference and page link (TWK-20) and the
 * attachment button (TWK-18) - and TipTap takes its extension list at
 * construction.
 *
 * ⚠️ THE FORMATTING EXTENSIONS ARE CONFIGURED IDENTICALLY, INCLUDING THE THREE
 * `class` VALUES, and a test compares the two files' HTMLAttributes blocks. They
 * cannot be shared through one exported constant: `wikiSanitise.test.js` reads
 * WikiPageEditor.js's OWN source to prove every class it configures is on the
 * sanitiser's allow-list, so moving the list out of that file would make that
 * guard pass vacuously - a security check silently answering about nothing.
 *
 * ⚠️ `injectCSS: false` for the same reason as the client editor: TipTap injects
 * its base stylesheet as a <style> element, which a CSP of style-src 'self'
 * blocks. Those rules live in index.css.
 */

const TmeWikiPageEditor = ({
    content,
    title,
    onChange,
    onImageUpload,
    onDocumentUpload,
    onInsertScriptRef,
    onInsertPageLink,
    onDeleteMedia,
    editorRef,
    readOnly = false,
    placeholder = 'Write the procedure...',
    debounceMs = 1500
}) => {
    const debounceTimer = useRef(null);
    const imageInputRef = useRef(null);
    const documentInputRef = useRef(null);

    // ⛔ THE OPTION IS CONFIGURED ONCE AND MUST NOT CLOSE OVER A RENDER'S VALUES
    // (TWM §7.1.1). `useEditor` below is called with NO dependency array, so the
    // extension list is built once - a raw `onDeleteMedia` would be captured
    // from the FIRST render, when the pane's `page` is still null and its save()
    // returns immediately, and the bin would silently do nothing, on every page,
    // forever. Adding deps to useEditor is not the fix: it would tear down and
    // rebuild the editor, losing the document and the undo stack.
    //
    // ⚠️ Assigned during RENDER rather than in an effect, deliberately: the bin
    // can be clicked before any effect has run for the current page, and a
    // one-render-stale callback here is the whole defect.
    //
    // The rule for any future option on this node: a stable wrapper over a ref,
    // never a raw callback.
    const onDeleteMediaRef = useRef(null);
    onDeleteMediaRef.current = onDeleteMedia;
    const stableOnDeleteMedia = useRef((args) => onDeleteMediaRef.current?.(args)).current;

    const editor = useEditor({
        injectCSS: false,
        // ⛔ WITHOUT THIS THE TOOLBAR NEVER UPDATES AS THE CARET MOVES.
        // @tiptap/react@3 re-renders on a transaction ONLY when this is true
        // (dist/index.js: the useEditorState selector returns null otherwise) -
        // it defaulted the other way in v2, which is what every `isActive`
        // toolbar in this file was written against. So Bold's highlight, the
        // conditional Remove Link button and TWM's "Delete attached file" button
        // all read a state frozen at the last render.
        //
        // Found by TWM's test 29: the button is gated on the caret being inside
        // a media link, and it simply never appeared.
        shouldRerenderOnTransaction: true,
        extensions: [
            StarterKit.configure({
                codeBlock: false,
                blockquote: false,
                horizontalRule: false
            }),
            Underline,
            ResizableImage.configure({
                inline: false,
                // ⚠️ NO base64. A data: URI is stripped by the sanitiser on the
                // way in anyway (wikiSanitise undoes DOMPurify's DATA_URI_TAGS
                // carve-out), so allowing it here would only produce images that
                // vanish on save.
                allowBase64: false,
                // The bin hands the whole sequence to the pane (TWM D2). The
                // CLIENT wiki configures nothing here and its bin is unchanged.
                onDeleteMedia: stableOnDeleteMedia
            }),
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: 'text-primary-600 underline hover:text-primary-800'
                }
            }),
            Table.configure({
                resizable: true,
                handleWidth: 5,
                cellMinWidth: 80,
                lastColumnResizable: true,
                allowTableNodeSelection: true
            }),
            TableRow,
            TableCell.configure({ HTMLAttributes: {} }),
            TableHeader.configure({ HTMLAttributes: {} }),
            CodeBlock.configure({
                HTMLAttributes: {
                    class: 'bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm overflow-x-auto'
                }
            }),
            Blockquote.configure({
                HTMLAttributes: {
                    class: 'border-l-4 border-gray-300 pl-4 italic text-gray-600'
                }
            }),
            HorizontalRule,
            TextStyle,
            Color,
            Highlight.configure({ multicolor: true }),
            Placeholder.configure({ placeholder }),
            // ⚠️ THESE TWO ARE WHY THIS EDITOR EXISTS SEPARATELY FROM
            // WikiPageEditor - the client wiki must never carry them.
            //
            // Their position in this array does NOT decide whether they beat
            // Link's `a[href]` rule; the PARSE-RULE priority inside each node
            // does, and each says so at the rule. Measured, not assumed.
            ScriptRefNode,
            PageLinkNode
        ],
        content,
        editable: !readOnly,
        onUpdate: ({ editor: instance }) => {
            if (!onChange) return;
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
            debounceTimer.current = setTimeout(() => onChange(instance.getHTML()), debounceMs);
        }
    });

    useEffect(() => {
        if (editor && content !== editor.getHTML()) {
            editor.commands.setContent(content, { emitUpdate: false });
        }
    }, [content, editor]);

    useEffect(() => {
        if (editor) editor.setEditable(!readOnly);
    }, [editor, readOnly]);

    // The pane inserts a node only AFTER the link row is written (§9.3), which
    // it cannot do without a handle on the editor.
    useEffect(() => {
        if (editorRef) editorRef.current = editor;
    }, [editor, editorRef]);

    useEffect(() => () => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
    }, []);

    if (!editor) {
        return (
            <div className="border border-gray-300 rounded-lg p-4 min-h-64 bg-gray-50 flex items-center justify-center">
                <span className="text-gray-400">Loading editor...</span>
            </div>
        );
    }

    const ToolbarButton = ({ onClick, isActive, disabled, title: buttonTitle, children }) => (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={buttonTitle}
            className={`p-2 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                isActive ? 'bg-gray-200 text-primary-600' : 'text-gray-600'
            }`}
        >
            {children}
        </button>
    );

    const ToolbarDivider = () => <div className="w-px h-6 bg-gray-300 mx-1" />;

    /**
     * An uploaded image becomes an <img>; an uploaded document becomes an
     * ANCHOR carrying its own filename - which is what the reader clicks, and
     * what `Content-Disposition: attachment` then downloads rather than opening
     * inline.
     */
    const handleImageFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || !onImageUpload) return;
        const url = await onImageUpload(file);
        if (url) editor.chain().focus().setImage({ src: url }).run();
    };

    const handleDocumentFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || !onDocumentUpload) return;
        const result = await onDocumentUpload(file);
        if (!result?.url) return;
        // A NODE, not an HTML string (review finding 10). The filename is the
        // user's own, and interpolated into markup a `<` or `&` in it was
        // parsed as markup rather than shown. As a text node under a link mark
        // it is text whatever it contains.
        editor.chain().focus()
            .insertContent({
                type: 'text',
                text: result.filename || 'attachment',
                marks: [{ type: 'link', attrs: { href: result.url } }]
            })
            .run();
    };

    /**
     * D3: an attachment is an anchor, so there is nothing to hang a button on -
     * it gets a TOOLBAR button that appears when the caret is inside a link
     * whose href is a media path. Not a right-click menu; nothing else in this
     * portal has one.
     */
    const mediaHref = editor.isActive('link') ? editor.getAttributes('link').href : null;
    const isMediaLink = typeof mediaHref === 'string' && mediaHref.startsWith('/tme-wiki-media/');

    /**
     * Hand the pane the anchor's href, its range and its text.
     *
     * ⚠️ THE RANGE IS CAPTURED HERE, NOT RE-DERIVED LATER. The pane removes the
     * anchor, awaits a save that can take ~7 s, and puts it back if the save
     * refuses - by which time a selection-relative command would act on wherever
     * the caret has since gone. `extendMarkRange('link')` first, exactly as
     * setLink does above.
     */
    const deleteAttachment = () => {
        if (!onDeleteMedia) return;
        editor.chain().focus().extendMarkRange('link').run();
        const { from, to } = editor.state.selection;
        const text = editor.state.doc.textBetween(from, to);
        onDeleteMedia({
            src: mediaHref,
            link: { from, to, text, href: mediaHref },
            deleteNode: () => editor.chain().focus()
                .setTextSelection({ from, to })
                .unsetLink()
                .deleteSelection()
                .run()
        });
    };

    const setLink = () => {
        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('Enter URL:', previousUrl);
        if (url === null) return;
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    };

    return (
        <div className="border border-gray-300 rounded-lg overflow-hidden">
            {!readOnly && (
                <div className="bg-gray-50 border-b border-gray-300 p-2 flex items-center flex-wrap gap-0.5">
                    <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Bold">
                        <FaBold className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Italic">
                        <FaItalic className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive('underline')} title="Underline">
                        <FaUnderline className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive('strike')} title="Strikethrough">
                        <FaStrikethrough className="w-4 h-4" />
                    </ToolbarButton>

                    <ToolbarDivider />

                    {[1, 2, 3].map(level => (
                        <ToolbarButton
                            key={level}
                            onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
                            isActive={editor.isActive('heading', { level })}
                            title={`Heading ${level}`}
                        >
                            <span className="text-xs font-bold">{`H${level}`}</span>
                        </ToolbarButton>
                    ))}

                    <ToolbarDivider />

                    <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Bullet List">
                        <FaListUl className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Numbered List">
                        <FaListOl className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} isActive={editor.isActive('blockquote')} title="Quote">
                        <FaQuoteRight className="w-4 h-4" />
                    </ToolbarButton>

                    <ToolbarDivider />

                    <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal Rule">
                        <FaMinus className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} isActive={editor.isActive('codeBlock')} title="Code Block">
                        <FaCode className="w-4 h-4" />
                    </ToolbarButton>

                    <ToolbarDivider />

                    <ToolbarButton onClick={setLink} isActive={editor.isActive('link')} title="Link">
                        <FaLink className="w-4 h-4" />
                    </ToolbarButton>
                    {editor.isActive('link') && (
                        <ToolbarButton onClick={() => editor.chain().focus().unsetLink().run()} title="Remove Link">
                            <FaUnlink className="w-4 h-4" />
                        </ToolbarButton>
                    )}
                    {/* ⚠️ "Delete attached file", DISTINCT from "Remove Link"
                        beside it: one takes the link off the text, the other
                        destroys the file. The title is on the BUTTON - a
                        react-icons `title` renders a <title> CHILD, which
                        NativeTooltips cannot lift, so the browser draws its own
                        blue default. */}
                    {isMediaLink && (
                        <ToolbarButton
                            onClick={deleteAttachment}
                            title="Delete attached file"
                            disabled={!onDeleteMedia}
                        >
                            <FaTrash className="w-4 h-4" />
                        </ToolbarButton>
                    )}
                    <ToolbarButton
                        onClick={onInsertScriptRef}
                        title="Insert a script reference"
                        disabled={!onInsertScriptRef}
                    >
                        <FaFileCode className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={onInsertPageLink}
                        title="Insert a link to another wiki page"
                        disabled={!onInsertPageLink}
                    >
                        <FaFileAlt className="w-4 h-4" />
                    </ToolbarButton>

                    <ToolbarButton
                        onClick={() => imageInputRef.current?.click()}
                        title="Insert Image"
                        disabled={!onImageUpload}
                    >
                        <FaImage className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => documentInputRef.current?.click()}
                        title="Attach a document"
                        disabled={!onDocumentUpload}
                    >
                        <FaPaperclip className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                        title="Insert Table"
                    >
                        <FaTable className="w-4 h-4" />
                    </ToolbarButton>

                    <ToolbarDivider />

                    <ToolbarButton
                        onClick={() => {
                            const colour = window.prompt('Enter colour (e.g., #ff0000, red):', '#000000');
                            if (colour) editor.chain().focus().setColor(colour).run();
                        }}
                        title="Text Colour"
                    >
                        <FaFont className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => {
                            const colour = window.prompt('Enter highlight colour (e.g., #ffff00, yellow):', '#ffff00');
                            if (colour) editor.chain().focus().toggleHighlight({ color: colour }).run();
                        }}
                        isActive={editor.isActive('highlight')}
                        title="Highlight"
                    >
                        <FaHighlighter className="w-4 h-4" />
                    </ToolbarButton>

                    <ToolbarDivider />

                    <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
                        <FaUndo className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
                        <FaRedo className="w-4 h-4" />
                    </ToolbarButton>

                    {/* The accept lists mirror the server's allow-list (§5.7).
                        They are a convenience: the server refuses anything else
                        on the MIME type AND on the extension, since either alone
                        is bypassable. */}
                    <input
                        ref={imageInputRef}
                        type="file"
                        data-testid="tme-wiki-image-input"
                        accept="image/png,image/jpeg,image/gif,image/webp"
                        className="hidden"
                        onChange={handleImageFile}
                    />
                    <input
                        ref={documentInputRef}
                        type="file"
                        data-testid="tme-wiki-document-input"
                        accept="application/pdf,text/plain,text/csv,application/json,application/zip,.log"
                        className="hidden"
                        onChange={handleDocumentFile}
                    />
                </div>
            )}

            <div className="p-4">
                <div className="wiki-doc-card min-h-64">
                    {title && <h1 className="wiki-doc-title">{title}</h1>}
                    <div className="wiki-doc focus:outline-none">
                        <EditorContent editor={editor} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TmeWikiPageEditor;
